import React, { useState, useEffect, useMemo } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import OntologyLayout, {
  PLACEHOLDER_HEIGHT
} from 'components_new/templates/internal/Ontology'
import ImportSection from 'components_new/templates/internal/Ontology/ImportSection'

import { useSIMActions } from 'redux/actions/DataPlatform/SIM'
import { useSourceSchemaActions } from 'redux/actions/DataPlatform/SourceSchemasV2'
import { useSIMWithSources, useSIMStatus } from 'redux/hooks/SIM'
import {
  useShouldFetchSourceSchema,
  useSourcesFetched
} from 'redux/hooks/SimTables'
import { Color } from 'themes'

const SOURCE_COLORS: Color[] = [
  'primary',
  'secondary',
  'tertiary',
  'quaternary',
  'quintary',
  'senary',
  'septenary',
  'octonary',
  'nonary',
  'denary'
]

const getSourceColor = (source: string): Color => {
  const hash = source
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0)

  return SOURCE_COLORS[hash % SOURCE_COLORS.length]
}

/**
 * Overview of the custom information model, rendered on the new SIM board.
 * Shares the SIM redux slice with the standard overview (they live on
 * separate routes), fetching with type='custom'. Editing is intentionally
 * absent — the board is view-only for custom until its dedicated edit story;
 * the only change allowed is deleting the whole model.
 */
const OntologyCustom = () => {
  const { tryGetSIM, tryStartSIMImport, tryPollSIMImport, tryDeleteCustomSIM } =
    useSIMActions()

  const sim = useSIMWithSources()
  const simStatus = useSIMStatus()

  const { tryGetSourceSchemas } = useSourceSchemaActions()
  const shouldFetchSourceSchemas = useShouldFetchSourceSchema()
  const sourcesFetched = useSourcesFetched()

  const [filterType, setFilterType] = useState('all')
  const [search, setSearch] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [pollingIntervalId, setPollingIntervalId] = useState<ReturnType<
    typeof setInterval
  > | null>(null)

  useEffect(() => {
    // Shared slice with the standard overview: refetch whenever the loaded
    // ontology isn't the custom one (first load, or returning from standard).
    if (sim?.type !== 'CUSTOM_ONTOLOGY') {
      tryGetSIM('custom')
    }
  }, [])

  useEffect(() => {
    if (shouldFetchSourceSchemas) {
      tryGetSourceSchemas()
    }
  }, [shouldFetchSourceSchemas])

  const isFetched = simStatus === 'success' && !!sourcesFetched

  const emptyContent = useMemo(() => {
    if (simStatus === 'error') {
      return <ErrorPlaceholder code={500} sx={{ height: PLACEHOLDER_HEIGHT }} />
    }

    if (simStatus === 'notFound') {
      return (
        <EmptyPlaceholder
          background={true}
          height={PLACEHOLDER_HEIGHT}
          icon="AccountTreeOutlined"
          size="medium"
          title="Ingen anpassad informationsmodell"
          body="Kunden har ingen anpassad modell — se Standard-fliken."
        />
      )
    }

    if (isFetched && sim && sim.tables.length === 0) {
      return (
        <EmptyPlaceholder
          background={true}
          height={PLACEHOLDER_HEIGHT}
          icon="AccountTreeOutlined"
          size="medium"
          title="Informationsmodellen saknar tabeller"
        />
      )
    }

    return undefined
  }, [simStatus, isFetched, sim])

  const tables = useMemo(() => {
    if (!isFetched || !sim) {
      return []
    }

    return sim.tables
      .filter((table) => {
        let toReturn = true

        if (filterType === 'modeled') {
          toReturn = table.source_tables.length > 0
        } else if (filterType === 'unmodeled') {
          toReturn = table.source_tables.length === 0
        }

        if (search) {
          const displayName = table.alias ?? table.name

          toReturn =
            toReturn && displayName.toLowerCase().includes(search.toLowerCase())
        }

        return toReturn
      })
      .map((table) => (
        <GenericListItem
          action="to"
          key={table.id}
          size="small"
          flexTitle
          title={table.name}
          body={table.alias ? table.name : undefined}
          titleAdornment={
            table.source_names.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {table.source_names.map((source) => (
                  <Chip
                    key={source}
                    size={'xsmall'}
                    sx={{ ml: 1 }}
                    label={source}
                    color={getSourceColor(source)}
                  />
                ))}
              </Box>
            ) : undefined
          }
          to={`/data-platform/custom/tables/${table.id}`}
          variant="entity"
        />
      ))
  }, [isFetched, filterType, search, sim])

  const handleDelete = () => {
    setDeleting(true)
    setDeleteError(null)

    tryDeleteCustomSIM((err) => {
      setDeleting(false)

      if (err) {
        setDeleteError(
          err === 'error' ? 'Det gick inte att ta bort modellen.' : err
        )
      } else {
        setDeleteOpen(false)
      }
    })
  }

  return (
    <OntologyLayout
      currentType="custom"
      isFetched={isFetched}
      search={search}
      onSearchChange={setSearch}
      filterType={filterType}
      onFilterTypeChange={setFilterType}
      topContent={
        sim ? (
          <ImportSection
            id={sim.id}
            latestRun={sim.latest_run}
            onStart={(id) => tryStartSIMImport(id)}
            onPoll={(id) => tryPollSIMImport(id)}
            pollingIntervalId={pollingIntervalId}
            setPollingIntervalId={setPollingIntervalId}
          />
        ) : undefined
      }
      filterActions={
        <Button
          color="error"
          onClick={() => setDeleteOpen(true)}
          startIcon={<Icon name="Delete" />}
          variant="text"
        >
          Ta bort modell
        </Button>
      }
      emptyContent={emptyContent}
    >
      <>
        {tables}
        <SimpleDialog
          actions={
            <>
              <Button
                disabled={deleting}
                onClick={() => setDeleteOpen(false)}
                variant="text"
              >
                Avbryt
              </Button>
              <Button
                color="error"
                loading={deleting}
                onClick={handleDelete}
                variant="contained"
              >
                Ta bort
              </Button>
            </>
          }
          onClose={() => setDeleteOpen(false)}
          open={deleteOpen}
          title="Ta bort anpassad informationsmodell?"
        >
          <Text>
            Hela den anpassade informationsmodellen tas bort, tillsammans med
            alla tabeller och relationer. Redan skapad data påverkas inte. Det
            går inte att ångra.
          </Text>
          {deleteError ? (
            <Text color="error.main" sx={{ mt: 2 }}>
              {deleteError}
            </Text>
          ) : null}
        </SimpleDialog>
      </>
    </OntologyLayout>
  )
}

export default OntologyCustom
